import { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { fetchAPI, requestAPI } from "../../../utils/utils";
import './NotificationGroupManager.css';

// Changes show at once and are sent one after another, so quick additions never overwrite each other.
function NotificationGroupManager({ user_names, onGroupsChange }) {
    const [groups, setGroups] = useState([]);
    const [newGroupName, setNewGroupName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [editingGroup, setEditingGroup] = useState(null);
    const [editingName, setEditingName] = useState('');
    const pendingRequests = useRef(Promise.resolve());

    const loadGroups = () => {
        fetchAPI('/notification_groups', (data) => {
            setGroups(Array.isArray(data) ? data : Object.values(data || {}));
            setIsLoading(false);
        }, false, () => setIsLoading(false));
    };

    useEffect(() => {
        setIsLoading(true);
        loadGroups();
    }, []);

    useEffect(() => {
        if (!isLoading && onGroupsChange) onGroupsChange(groups);
    }, [groups, isLoading]);

    const send = (request) => {
        pendingRequests.current = pendingRequests.current
            .then(request)
            .catch(error => {
                console.error('Notification group error:', error);
                alert("No s'han pogut desar els canvis del grup. Torna-ho a provar.");
                loadGroups();
            });
    };

    const createGroup = () => {
        const name = newGroupName.trim();
        if (!name) return;
        if (groups.some(g => g.name === name)) {
            alert('Ja existeix un grup amb aquest nom.');
            return;
        }

        setGroups(prev => [...prev, { name, ids: [] }]);
        setNewGroupName('');
        send(() => requestAPI('POST', '/notification_groups', { name, ids: [] }));
    };

    const handleGroupChange = (name, selectedUsers) => {
        const ids = selectedUsers.map(u => u.value);
        setGroups(prev => prev.map(g => g.name === name ? { ...g, ids } : g));
        send(() => requestAPI('PUT', `/notification_groups/${encodeURIComponent(name)}`, { name, ids }));
    };

    const deleteGroup = (name) => {
        const conf = window.confirm(`Segur que vols eliminar el grup: "${name}"?`);
        if (!conf) return;

        setGroups(prev => prev.filter(g => g.name !== name));
        send(() => requestAPI('DELETE', `/notification_groups/${encodeURIComponent(name)}`));
    };

    const startEditing = (name) => {
        setEditingGroup(name);
        setEditingName(name);
    };

    const cancelEditing = () => {
        setEditingGroup(null);
        setEditingName('');
    };

    const saveGroupName = (oldName, ids) => {
        const newName = editingName.trim();
        if (!newName || newName === oldName) {
            cancelEditing();
            return;
        }

        if (groups.some(g => g.name === newName)) {
            alert('Ja existeix un grup amb aquest nom.');
            return;
        }

        // The new group is written before the old one is removed, so a failure never loses its members
        setGroups(prev => prev.map(g => g.name === oldName ? { ...g, name: newName, ids: ids || [] } : g));
        cancelEditing();
        send(() => requestAPI('POST', '/notification_groups', { name: newName, ids: ids || [] })
            .then(() => requestAPI('DELETE', `/notification_groups/${encodeURIComponent(oldName)}`)));
    };

    return (
        <div className="notification-group-manager">
            <section className="create-group-section">
                <h2 className="section-title">Crea un nou grup</h2>
                <div className="create-group-form">
                    <input
                        className="group-name-input"
                        placeholder="Nom del nou grup"
                        value={newGroupName}
                        onChange={e => setNewGroupName(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                createGroup();
                            }
                        }}
                    />
                    <button
                        className="create-group-button"
                        onClick={createGroup}
                        disabled={!newGroupName.trim()}
                    >
                        Crea un nou grup
                    </button>
                </div>
            </section>

            <section className="groups-list-section">
                <h2 className="section-title">Grups creats</h2>

                {isLoading ? (
                    <div className="groups-loading">
                        <div className="loading-spinner" />
                        <span>Carregant grups...</span>
                    </div>
                ) : groups.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-users empty-icon" />
                        <p>No hi ha cap grup creat</p>
                        <span className="empty-hint">Crea el teu primer grup per enviar notificacions ràpidament a un conjunt de persones.</span>
                    </div>
                ) : (
                    <div className="groups-list">
                        {groups.map(({ name, ids }) => {
                            const usersOfGroup = (ids || [])
                                .map((id) => user_names.find((u) => parseInt(u.value) === parseInt(id)))
                                .filter(u => u !== undefined);

                            return (
                                <div className="group-card" key={name}>
                                    <div className="group-card-header">
                                        {editingGroup === name ? (
                                            <div className="group-name-edit">
                                                <input
                                                    className="group-name-edit-input"
                                                    value={editingName}
                                                    onChange={e => setEditingName(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') saveGroupName(name, ids);
                                                        if (e.key === 'Escape') cancelEditing();
                                                    }}
                                                    autoFocus
                                                />
                                                <button
                                                    className="group-name-save-button"
                                                    onClick={() => saveGroupName(name, ids)}
                                                    title="Desar"
                                                >
                                                    <i className="fas fa-check"></i>
                                                </button>
                                                <button
                                                    className="group-name-cancel-button"
                                                    onClick={cancelEditing}
                                                    title="Cancel·lar"
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="group-name-display">
                                                <h3 className="group-name">{name}</h3>
                                                <button
                                                    className="edit-group-button"
                                                    onClick={() => startEditing(name)}
                                                    title="Editar nom"
                                                >
                                                    <i className="fas fa-pencil-alt"></i>
                                                </button>
                                            </div>
                                        )}
                                        <button
                                            className="delete-group-button"
                                            onClick={() => deleteGroup(name)}
                                            title="Eliminar grup"
                                        >
                                            Borra el grup
                                        </button>
                                    </div>
                                    <div className="group-card-body">
                                        <Select
                                            options={user_names}
                                            isMulti
                                            name="users"
                                            className="group-select"
                                            classNamePrefix="select"
                                            value={usersOfGroup}
                                            onChange={(selectedUsers) => handleGroupChange(name, selectedUsers || [])}
                                            placeholder="Afegeix membres al grup..."
                                            noOptionsMessage={() => "No s'han trobat castellers"}
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                        <span className="members-count">
                                            {usersOfGroup.length} {usersOfGroup.length === 1 ? 'membre' : 'membres'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}

export default NotificationGroupManager;